import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Select, MenuItem, FormControl, InputLabel,
  Grid, Alert, CircularProgress, Typography, Box,
} from '@mui/material';
import type { Study, OrderInfo, PatientBean } from '../../api/types';
import { api } from '../../api/rpc';

interface OrderEditorDialogProps {
  open: boolean;
  study: Study | null;
  mode: 'edit' | 'add';
  onClose: () => void;
  onComplete: () => void;
}

export const OrderEditorDialog = ({ open, study, mode, onClose, onComplete }: OrderEditorDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [patient, setPatient] = useState<PatientBean | null>(null);
  const [order, setOrder] = useState<OrderInfo | null>(null);

  // Form fields
  const [procedureCode, setProcedureCode] = useState('');
  const [procedureDescr, setProcedureDescr] = useState('');
  const [modality, setModality] = useState('');
  const [bodyPart, setBodyPart] = useState('');
  const [priority, setPriority] = useState('ROUTINE');
  const [reasonForStudy, setReasonForStudy] = useState('');
  const [clinicalInfo, setClinicalInfo] = useState('');
  const [orderingProvider, setOrderingProvider] = useState('');

  useEffect(() => {
    if (!open || !study) return;
    setLoading(true);
    setError(null);

    const loadData = async () => {
      try {
        // Load patient
        if (study.patientKey) {
          const pb = await api.getPatientBean(study.patientKey);
          setPatient(pb);
        }
        // Load existing order for edit mode
        if (mode === 'edit') {
          const oi = await api.getOrder(study.objKey);
          setOrder(oi);
          if (oi?.detailOrder) {
            setProcedureCode(oi.detailOrder.procedureCode ?? '');
            setProcedureDescr(oi.detailOrder.procedureCodeDescr ?? oi.detailOrder.universalServiceIdDescr ?? '');
            setModality(study.mod ?? '');
            setBodyPart(study.bod ?? '');
            setPriority(oi.detailOrder.priority ?? 'ROUTINE');
            setReasonForStudy(oi.detailOrder.reasonForStudy ?? '');
            setClinicalInfo(oi.detailOrder.relevantClinicalInfo ?? '');
          }
          if (oi?.commonOrder) {
            setOrderingProvider(oi.commonOrder.orderingProvider ?? '');
          }
        } else {
          // Add mode — preset from study
          setModality(study.mod ?? '');
          setBodyPart(study.bod ?? '');
          setReasonForStudy(study.reason ?? study.reasonForExam ?? '');
          setOrderingProvider(study.phy ?? '');
          setProcedureCode('');
          setProcedureDescr('');
          setPriority('ROUTINE');
          setClinicalInfo(study.history ?? '');
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [open, study, mode]);

  const handleSave = async () => {
    if (!study) return;
    setSaving(true);
    setError(null);
    try {
      const orderInfo: OrderInfo = {
        commonOrder: {
          ...order?.commonOrder,
          orderingProvider,
        },
        detailOrder: {
          ...order?.detailOrder,
          procedureCode,
          procedureCodeDescr: procedureDescr,
          universalServiceIdDescr: procedureDescr,
          priority,
          reasonForStudy,
          relevantClinicalInfo: clinicalInfo,
        },
        patientVisit: order?.patientVisit,
      };

      if (mode === 'add') {
        await api.addOrders(
          study.patientKey ?? '',
          [orderInfo],
          [{
            site: study.site,
            phy: orderingProvider || study.phy,
            mod: modality,
            bod: bodyPart,
            reasonForExam: reasonForStudy,
            history: clinicalInfo,
          }],
        );
      } else {
        // For edit, update insurances (which also saves order changes)
        await api.updateInsurances(study.objKey, orderInfo, study.patientKey);
      }
      onComplete();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save order');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{mode === 'edit' ? 'Edit Order' : 'Add Order'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {patient && (
              <Box sx={{ mb: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>Patient:</strong> {patient.niceName ?? patient.name} | ID: {patient.id} |
                  DOB: {patient.dob ?? 'N/A'} | Sex: {patient.sex ?? 'N/A'}
                </Typography>
              </Box>
            )}
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid size={{ xs: 6 }}>
                <TextField
                  label="Procedure Code"
                  value={procedureCode}
                  onChange={e => setProcedureCode(e.target.value)}
                  fullWidth size="small"
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  label="Procedure Description"
                  value={procedureDescr}
                  onChange={e => setProcedureDescr(e.target.value)}
                  fullWidth size="small"
                />
              </Grid>
              <Grid size={{ xs: 4 }}>
                <TextField
                  label="Modality"
                  value={modality}
                  onChange={e => setModality(e.target.value)}
                  fullWidth size="small"
                />
              </Grid>
              <Grid size={{ xs: 4 }}>
                <TextField
                  label="Body Part"
                  value={bodyPart}
                  onChange={e => setBodyPart(e.target.value)}
                  fullWidth size="small"
                />
              </Grid>
              <Grid size={{ xs: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={priority}
                    label="Priority"
                    onChange={e => setPriority(e.target.value)}
                  >
                    <MenuItem value="STAT">STAT</MenuItem>
                    <MenuItem value="ROUTINE">Routine</MenuItem>
                    <MenuItem value="URGENT">Urgent</MenuItem>
                    <MenuItem value="ASAP">ASAP</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  label="Ordering Provider"
                  value={orderingProvider}
                  onChange={e => setOrderingProvider(e.target.value)}
                  fullWidth size="small"
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  label="Reason for Study"
                  value={reasonForStudy}
                  onChange={e => setReasonForStudy(e.target.value)}
                  fullWidth size="small"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Clinical Information"
                  value={clinicalInfo}
                  onChange={e => setClinicalInfo(e.target.value)}
                  fullWidth multiline rows={3} size="small"
                />
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading || saving}>
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
